import { useMutation, useQuery } from '@tanstack/react-query'
import { AlertCircle, Lock } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { invitationAPI } from '../api/index'
import { Button } from '../components/Button'
import { useAuthStore } from '../store/auth'

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const setAuth = useAuthStore(s => s.setAuth)

  const token = searchParams.get('token')
  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordStrength, setPasswordStrength] = useState(0)

  const { data: invitation, isLoading: invitationLoading, error: invitationError } = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => invitationAPI.validate(token!).then(r => r.data),
    enabled: !!token,
  })

  const acceptMutation = useMutation({
    mutationFn: () =>
      invitationAPI.accept({
        token: token!,
        full_name: fullName,
        password,
      }),
    onSuccess: (response) => {
      setAuth(response.data, response.data.user)
      toast.success('Welcome! Signing you in...')
      setTimeout(() => navigate('/dashboard'), 1500)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to accept invitation')
    },
  })

  const calculatePasswordStrength = (pass: string) => {
    let strength = 0
    if (pass.length >= 8) strength++
    if (pass.length >= 12) strength++
    if (/[a-z]/.test(pass) && /[A-Z]/.test(pass)) strength++
    if (/\d/.test(pass)) strength++
    if (/[^a-zA-Z0-9]/.test(pass)) strength++
    setPasswordStrength(Math.min(5, strength))
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value
    setPassword(newPassword)
    calculatePasswordStrength(newPassword)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!fullName.trim()) {
      toast.error('Please enter your name')
      return
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    acceptMutation.mutate()
  }

  if (!token) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />
          <p className="text-gray-600">Invalid invitation link</p>
        </div>
      </div>
    )
  }

  if (invitationLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="h-12 w-12 bg-gray-200 rounded-full animate-pulse mx-auto" />
          <p className="text-gray-600 mt-4">Validating invitation...</p>
        </div>
      </div>
    )
  }

  if (invitationError) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />
          <p className="text-gray-600 mb-4">Invalid or expired invitation</p>
          <Button onClick={() => navigate('/login')} className="bg-saffron hover:bg-orange-600">
            Back to Login
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-8 shadow-sm">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Welcome!</h1>
            <p className="text-gray-600 mt-2">
              You've been invited to join <span className="font-semibold text-gray-900">{invitation?.agency_name || 'RTO Manager'}</span>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email (Read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                disabled
                value={invitation?.email || ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
              />
            </div>

            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name*</label>
              <input
                type="text"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-saffron focus:outline-none"
                placeholder="Enter your full name"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password*</label>
              <div className="relative">
                <input
                  type="password"
                  required
                  value={password}
                  onChange={handlePasswordChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-saffron focus:outline-none pr-10"
                  placeholder="At least 8 characters"
                />
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>

              {/* Password Strength Bar */}
              {password && (
                <div className="mt-2">
                  <div className="flex gap-1 h-1.5">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div
                        key={i}
                        className={`flex-1 rounded-full ${
                          i <= passwordStrength
                            ? passwordStrength >= 4
                              ? 'bg-green-500'
                              : passwordStrength >= 3
                              ? 'bg-yellow-500'
                              : 'bg-orange-500'
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    {passwordStrength < 3
                      ? 'Weak password'
                      : passwordStrength < 5
                      ? 'Good password'
                      : 'Strong password'}
                  </p>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password*</label>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none ${
                  confirmPassword && password !== confirmPassword
                    ? 'border-red-300 focus:border-red-500'
                    : 'border-gray-300 focus:border-saffron'
                }`}
                placeholder="Confirm your password"
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={acceptMutation.isPending || !fullName || password.length < 8 || password !== confirmPassword}
              className="w-full bg-saffron hover:bg-orange-600 text-white mt-6"
            >
              {acceptMutation.isPending ? 'Creating account...' : 'Accept & Sign In'}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-xs text-gray-500 text-center mt-6">
            By accepting this invitation, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  )
}
